import { WebToNativeMessaging } from './web-to-native-messaging';
import { NativeToWebMessaging } from './native-to-web-messaging';

/**
 * Configuration for a button that the web application will command the native application to display.
 * It may be rendered as text, an icon, or both depending on the OS and 
 * mobile layout configuration, so both a label and icon must always be provided.
 */
export interface INativeAppButton {
  /** the text that will appear on the button (if the button is rendered with text) */
  label: string;
  /**
   * The unique indentifier for this button.
   * This string will be used to identify a callback exposed on `window.mobileHooks`
   * that the native application will run when this button is clicked.
   */
  id: string;
  /**
   * The name of an Everest Design System icon to render on the button
   * (if the button is rendered with an icon).
   * Not all of the web Everest icons are available to use in native apps.
   * When in doubt, ask a native app dev which icons are available to use,
   * or if they can add one.
   * @example `checkmark`, `save`
   */
  icon: string;

  /**
   * Primary button with a 0 index order will appear on the top right side of the application's title/navigation bar.
   * On Android, buttons with index order > 0 will be placed to the left of the primary button as an icon,
   * or to the right in an overflow menu as text.  
   * On iOS, buttons with an index order > 0 will be placed in a lower toolbar, and may be rendered as
   * text or icons depending on how many there are.
   */
  order: number;
}

/**
 * Configuration for several aspects of a mobile app's screen that the web application controls
 */
interface IMobileLayoutConfiguration {
  /** the text to render in the native application's title bar/navigation bar */
  title: string;
  /** the list of buttons for the native application to render (not including the back/cancel button) */
  buttons: INativeAppButton[];
  /** the type of button to display on the left of the iOS navigation bar  */
  backButtonType: 'back' | 'cancel';
}

/**
 * Configuration for the native confirmation dialog triggered by a web application.
 */
interface IConfirmationDialogConfiguration {
  title: string,
  body: string,
  confirmButton: {
    /** 
     * The unique indentifier for this button.
     * This string will be used to identify a callback exposed on `window.mobileHooks`
     * that the native application will run when the right button is clicked.
     */
    id: string;
    /** the text to display on the "positive" or "confirm" button on the right side */
    label: string,
  },
  cancelButton: {
    /** The unique indentifier for this button.
     * This string will be used to identify a callback exposed on `window.mobileHooks`
     * that the native application will run when the left button is clicked.
     */
    id: string;
    /** the text to display on the "negative" or "cancel" button on the left side */
    label: string;
  }
}

/**
 * MobileHooks' methods build on top of {@link WebToNativeMessaging} & {@link NativeToWebMessaging},
 * creating a convenient way for web developers to configure and trigger actions in native applications.
 */
export class MobileHooks {

  /**
   * Call this before using other mobile hooks if the web application is running in a webview.
   * Skip calling it if running in a regular web browser to prevent `window.mobileHooks` from being created.
   * @see {@link NativeToWebMessaging.initializeMobileHooks} for more details.
   */
  static initialize() {
    NativeToWebMessaging.initializeMobileHooks()
  }

  /**
   * Show the mobile app's confirmation dialog on top of the webview.
   * @param config - the configuration for the confirmation dialog. See {@link IConfirmationDialogConfiguration}
   * @returns a promise that resolves with the id of the button that was tapped
   * 
   * @example
   * ```ts
   * showConfirmationDialog({
   *  title: getLabel('lblAreYouSure'),
   *  body: getLabel('lblYouHaveUnsavedChanges),
   *  confirmButton: {
   *    id: 'confirm',
   *    label: getLabel('lblConfirm')
   *  },
   *  cancelButton: {
   *    id: 'cancel',
   *    label: getLabel('lblCancel')
   *  }
   * })
   * ```
   */
  static showConfirmationDialog(config: IConfirmationDialogConfiguration): Promise<string> {
    const {
      title,
      body,
      confirmButton,
      cancelButton,
    } = config;

    return new Promise(resolve => {
      NativeToWebMessaging.setMobileHook(confirmButton.id, () => resolve(confirmButton.id))
      NativeToWebMessaging.setMobileHook(cancelButton.id, () => resolve(cancelButton.id))

      WebToNativeMessaging.sendMessage('show_confirmation_dialog', {
        title,
        body,
        confirmButton,
        cancelButton
      })
    }).then((result: string) => {
      delete window.mobileHooks[confirmButton.id]
      delete window.mobileHooks[cancelButton.id]
      return result
    })
  }

  /**
   * Configure the title, iOS back button, and buttons that will be displayed by the native application.
   * @param config - configuration for the native navigation/title bar and toolbar.
   * @see {@link IMobileLayoutConfiguration}
   * 
   * @example
   * ```ts
   * MobileHooks.setMobileLayoutConfiguration({
   *   backButtonType: 'back',
   *   title: getLabel('lblEditAddress'),
   *   buttons: [{
   *     icon: 'save',
   *     id: 'save',
   *     label: getLabel('lblSave')
   *   },
   *   {
   *     icon: 'checkmark',
   *     id: 'submit',
   *     label: getLabel('Submit'),
   *     order: 0
   *  }]
   * })
   * ```
   */
  static setMobileLayoutConfiguration(
    config: IMobileLayoutConfiguration
  ) {
    WebToNativeMessaging.sendMessage('set_mobile_layout_configuration', config)
  }

  /**
   * Tell the mobile application to close the webview, and go back to the previous screen.
   * 
   * @remarks
   * Before calling this, consider reminding the user to save any unsaved work using `showConfirmationDialog`.
   */
  static closeWebview() {
    WebToNativeMessaging.sendMessage('close_webview', {})
  }

  /**
   * Set the JS function to run when the user presses the native application's back button.
   * This will be assigned to `window.mobileHooks.hybridGoBack`
   * If `window.mobileHooks.hybridGoBack` has been set already, it will be overwritten.
   * 
   * @param handleBackButtonTap - The function that takes the user back a step in
   * the web application's navigation.
   * It may trigger an asynchronous action and then return a boolean value immediately
   * (eg updating some React state).
   * Or it may do something synchronous and return a boolean value right after.
   * 
   * Either way the callback should return:
   * - `true` if the web application has handled the back button tap
   *    (eg. navigating back a screen, or triggering a confirmation dialog)
   * - `false` if the web application will not handle the back button tap and the
   *    native application can try to handle it instead (eg. closing the webview and navigating to a different screen)
   * 
   * @example 
   * When it is possible for web application to navigate backwards, the callback should return `true`
   * ```ts
   * const SomeModalWindowComponent = () => {
   * 
   *  const [isOpen, setIsOpen] = useState(false);
   * 
   *  // make the back button close the modal window
   *  MobileHooks.handleBackButtonTap(() => {
   *    setIsOpen(false)
   *    return true;
   *  })
   *  
   *   return isOpen ? <SomeComponent /> : null;
   * }
   * ```
   * 
   * @example 
   * When it is __not__ possible for web application to navigate backwards,
   * the callback should return `false`
   * ```ts
   * const App = () => {
   * 
   *  // let the native application handle this back button tap
   *  MobileHooks.handleBackButtonTap(() => {
   *    return false;
   *  })
   *  
   *   return <div>....
   * }
   * ```
   * 
   */
  static setBackButtonCallback(handleBackButtonTap: () => boolean) {
    NativeToWebMessaging.setMobileHook('hybridGoBack', handleBackButtonTap)
  }

  /**
   * Indicate to the native app that the web application has finished its initial load
   * and is ready to display.
   * 
   * @remarks
   * Web applications run this method either:
   * 
   * 1. after the web application has successfully loaded and rendering has stabilized
   * (eg web-based loading indicators are gone, Largest Contentful Paint is done)
   * 
   * 2. the web application throws an error on start-up and needs to show a
   * message to the user to help them decide what to do next
   * 
   * This differs from {@link showLoadingIndicator} in that it should only be
   * called once per webview creation since other initialization-related native
   * code might run in response to it. Use {@link showLoadingIndicator} for
   * loading indicators that are triggered by actions in the web application.
   * 
   * @example
   * ```ts
   * MobileHooks.showWebApplication()
   * ```
   */
  static showWebApplication() {
    WebToNativeMessaging.sendMessage('web_application_loading_complete', {})
  }

  /**
   * Set the visibility of the native application's loading indicator on top of the webview.
   * 
   * @remarks
   * This is different than {@link showWebApplication} in these ways:
   * 
   * 1. This can be called multiple times to show and hide the loading indicator
   * on multiple occasions.
   * 
   * 2. It doesn't have any deeper meaning or purpose than showing or hiding the
   * native loading indicator.
   * 
   * This will block interactivity in the webview, but does not stop people from
   * tapping buttons in the app's title/navigation bar that trigger other actions
   * in the web application. The web application needs to track whether the indicator
   * is visible, and whether or not it should respond to those native button taps.
   * 
   * @example
   * ```ts
   * MobileHooks.showLoadingIndicator(true)
   * try {
   *   await makeHttpRequest()
   * } catch() {
   *   // error handling
   * } finally {
   *   MobileHooks.showLoadingIndicator(false)
   * }
   * ```
   */
  static showLoadingIndicator(value: boolean) {
    WebToNativeMessaging.sendMessage('show_loading_indicator', { visible: value })
  }
}

